import { Box, Card, IconButton, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import { ReactComponent as ConfirmationIcon } from "../../../assets/icons/Confirmation.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/Images/Delete.svg";
import { ReactComponent as EditIcon } from "../../../assets/Images/EditIcon.svg";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import BaseButton from "../Common/Buttons/BaseButton";
import CustomScrollbars from "../CommonComponents/CustomScrollbars";
import ConfirmationPopup from "../CommonComponents/PopUpCards/ConfirmationPopup";

const CustomParams = ({ defaultFields, contactState, handleSave }) => {
  const { contact, loading } = contactState;
  const [inputValue, setInputValue] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const handleCustomParamsSave = () => {
    const rawList = inputValue
      .split(",")
      .map((val) => val.trim())
      .filter((val) => val && !defaultFields.includes(val));

    const uniqueNew = Array.from(new Set(rawList));

    if (uniqueNew.length === 0) {
      setErrMsg("Please enter at least one valid key.");
      setTimeout(() => setErrMsg(""), 3000);
      return;
    }

    let updatedContacts = [...contact];

    if (editingIndex !== null) {
      if (uniqueNew.length > 1) {
        setErrMsg("Please enter only one valid key.");
        setTimeout(() => setErrMsg(""), 3000);
        return;
      }
      updatedContacts[editingIndex] = uniqueNew[0];
    } else {
      const nonDefaultContacts = updatedContacts.filter(
        (c) => !defaultFields.includes(c)
      );
      updatedContacts = [
        ...defaultFields,
        ...Array.from(new Set([...uniqueNew, ...nonDefaultContacts])),
      ];
    }

    handleSave(updatedContacts, editingIndex !== null);
    setInputValue("");
    setEditingIndex(null);
  };

  const handleEdit = (item, idx) => {
    setInputValue(item);
    setEditingIndex(idx);
  };

  const handleCancelEdit = () => {
    setInputValue("");
    setEditingIndex(null);
  };

  const handleDelete = () => {
    if (deleteIndex !== null) {
      const updatedContacts = contact.filter((_, idx) => idx !== deleteIndex);
      handleSave(updatedContacts, false, true);
      setDeleteIndex(null);
      setShowDeletePopup(false);
    }
  };

  const confirmDelete = (idx) => {
    setDeleteIndex(idx);
    setShowDeletePopup(true);
  };

  return (
    <CustomScrollbars height="70vh">
      <Box sx={{ my: 1, pb: 2 }}>
        <Typography className="font-14-grey my-1">Parameters</Typography>
        <TextField
          fullWidth
          placeholder={
            editingIndex !== null
              ? "Edit parameter name"
              : "Enter comma-separated keys"
          }
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          sx={commonStyles.inputField}
        />
        {errMsg && (
          <Typography className="font-600-red py-1">{errMsg}</Typography>
        )}

        <Box className="flex-end" sx={{ gap: 2, mt: 2 }}>
          {editingIndex !== null && (
            <BaseButton
              title="Cancel"
              variant="outlined"
              handleClick={handleCancelEdit}
            />
          )}
          <BaseButton
            title={editingIndex !== null ? "Update" : "Save"}
            variant="contained"
            loading={loading}
            handleClick={handleCustomParamsSave}
          />
        </Box>
      </Box>

      {contact.length > 0 && (
        <>
          <Typography className="font-14-grey" sx={{ my: 1 }}>
            Existing Params
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 1.5,
            }}
          >
            {contact.map((item, idx) => (
              <Card
                key={idx}
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minWidth: 0,
                  boxShadow: 0,
                  borderRadius: 2,
                }}
              >
                <Typography
                  fontSize={14}
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    flexGrow: 1,
                  }}
                >
                  {item}
                </Typography>
                {!defaultFields.includes(item) && (
                  <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(item, idx)}
                      disabled={editingIndex !== null}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => confirmDelete(idx)}
                      disabled={editingIndex !== null}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Card>
            ))}
          </Box>
        </>
      )}
      <ConfirmationPopup
        show={showDeletePopup}
        toggle={() => setShowDeletePopup(false)}
        handleSave={handleDelete}
        message="Are you sure you want to delete this parameter?"
        heading="Delete Parameter"
        Icon={ConfirmationIcon}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </CustomScrollbars>
  );
};

export default CustomParams;
